import { Link, useParams } from 'react-router-dom';

import type { CreditDecisionResponse } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Card, CardBody } from '@fluxora/ui';

import { useUsageSelection } from '../hooks/useUsageSelection';
import { buildPlanStepPath } from '../services/funnel-rehydrate.service';
import { DepositBranch } from './DepositBranch';

/**
 * Renders exactly one of the three E-SF05 credit-decision branches (E-AC01
 * product-owner LOCKED model):
 *   - `approve`  — no deposit; proceed to sign (E-SF06, next epic).
 *   - `deposit`  — a deposit is required; pay it or elect a §25.478 waiver.
 *   - `waived`   — a statutory §25.478 waiver applied at decision time; no deposit.
 *
 * Prepaid is DEFERRED — there is deliberately no `prepaid` branch. The
 * discriminated union means the compiler enforces that every branch is handled
 * and none can be conflated.
 */
export interface CreditDecisionResultProps {
  readonly candidateId: string;
  readonly decision: CreditDecisionResponse;
  /**
   * Whole years, from the date of birth the customer entered. Used ONLY to decide
   * whether to offer the §25.478(e) age waiver — `undefined` when the DOB is unknown
   * (the normal state after a refresh), in which case the ground stays offered. Age
   * rather than the DOB itself: it is all the rule needs, so the PII does not travel.
   */
  readonly customerAgeYears?: number;
}

export function CreditDecisionResult({
  candidateId,
  decision,
  customerAgeYears,
}: CreditDecisionResultProps): JSX.Element {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  // "See your estimated bill" — advances a cleared customer to E-SF04. Slug is
  // present whenever this renders under /plan/:slug/credit; falls back to the
  // shelf if somehow absent.
  //
  // ⚠️ BUILT, NOT ASSEMBLED (QA-2026-08-06). This hand-written path was the ONE
  // funnel link that still dropped `?usage=&tier=`, so the estimate step was
  // reached with `?candidate=` alone — and a refresh there, or Back out of it,
  // lost the shopper's chosen kWh and re-defaulted the shelf to the §25.475
  // midpoint. `funnel-rehydrate.service` documents that its four builders are the
  // only things permitted to assemble one of these paths; this now is one.
  const selection = useUsageSelection();
  const toEstimate =
    slug === undefined
      ? '/'
      : buildPlanStepPath({ tariffId: slug, step: 'estimate', selection, candidateId });
  /* The cleared outcomes read as a MOMENT, not a log line: a centred panel
     in the funnel's pastel ring, with the forward step as a real button —
     the underlined "See your estimated bill →" string read as an afterthought
     under a toast. */
  const clearedPanel = (title: string, body: string): JSX.Element => (
    <div
      role="status"
      className="rounded-[calc(var(--r-lg)+1.5px)] bg-[image:var(--brand-gradient-tint)] p-[1.5px] shadow-card"
    >
      <Card variant="glass" padding="md" className="!rounded-[var(--r-lg)]">
        <CardBody className="flex flex-col items-center gap-3 p-0 py-5 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-success">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
              <path
                d="M5 12.5 10 17.5 19 7"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <h3 className="font-display text-xl font-bold tracking-tight text-foreground">{title}</h3>
          <p className="max-w-[52ch] text-sm leading-relaxed text-foreground/70">{body}</p>
          <Link to={toEstimate}>
            <Button variant="primary" size="lg" shape="pill" className="cta-sheen">
              {t('shop.credit.continueToEstimate')}
            </Button>
          </Link>
        </CardBody>
      </Card>
    </div>
  );

  if (decision.outcome === 'approve') {
    return clearedPanel(t('shop.credit.approve.title'), t('shop.credit.approve.body'));
  }

  if (decision.outcome === 'waived') {
    return clearedPanel(t('shop.credit.waived.title'), t('shop.credit.waived.body'));
  }

  return (
    <DepositBranch
      candidateId={candidateId}
      deposit={decision.deposit}
      customerAgeYears={customerAgeYears}
    />
  );
}
